<script setup>
import { Head } from '@inertiajs/vue3';
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { fontsHref, rootClass, rootStyle, templateFor, usesTrailheadBlocks } from './templates';
import { siteComponentsFor } from './siteComponents';
import { sitePrefix } from './siteLocation.js';
import { NO_POSTS_YET } from './blockContent.js';
import ThNewsIndex from './Trailhead/ThNewsIndex.vue';
import TemplatePreviewBar from './TemplatePreviewBar.vue';

const props = defineProps({
    // Title, description, share image and favicon, resolved
    // server-side by SiteMeta so every page type agrees.
    meta: { type: Object, default: () => ({}) },
    org: { type: Object, required: true },
    layout: { type: Object, required: true },
    posts: { type: Array, default: () => [] },
    site_data: { type: Object, default: () => ({}) },
    is_preview: { type: Boolean, default: false },
    preview_reason: { type: String, default: null },
});

// The chosen template's renderers. A template is a design only, so
// every block below reads the same config whichever one is picked.
const COMPONENTS = siteComponentsFor(props.layout.theme);
provide('tiqBlockComponents', COMPONENTS);
provide('tiqCurrentPost', null);
provide('tiqCurrentEvent', null);

// Trailhead draws this page from its own blocks; Classic keeps the
// markup below. A template is a design, so both read the same props.
const isTrailhead = usesTrailheadBlocks(templateFor(props.layout.theme).key);

const accent = computed(() => props.layout.theme?.accent || '#dd9b36');
const headerBlocks = computed(() => (props.layout.shared_header || []).filter((b) => COMPONENTS[b.type]));
const footerBlocks = computed(() => (props.layout.shared_footer || []).filter((b) => COMPONENTS[b.type]));

// Group posts by month label ("June 2026") just like EventsIndex —
// keeps the rhythm consistent across the two index pages.
function groupByMonth(posts) {
    const groups = [];
    let cur = null;
    for (const p of posts) {
        if (!p.published_at) continue;
        const d = new Date(p.published_at);
        const key = d.toLocaleDateString([], { month: 'long', year: 'numeric' });
        if (!cur || cur.label !== key) {
            cur = { label: key, items: [] };
            groups.push(cur);
        }
        cur.items.push(p);
    }
    return groups;
}
const groups = computed(() => groupByMonth(props.posts));

function fmtDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
}

// Canonical /site/{slug}/ link-rewriting (same logic as Show.vue).
const basePath = ref('');
function detectBase() {
    basePath.value = sitePrefix();
}
function onClick(e) {
    if (!basePath.value) return;
    const a = e.target.closest && e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    if (href.startsWith(basePath.value + '/') || href === basePath.value || href === basePath.value + '/') return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    window.location.href = basePath.value + href;
}
onMounted(() => { detectBase(); document.addEventListener('click', onClick); });
onBeforeUnmount(() => document.removeEventListener('click', onClick));
</script>

<template>
    <Head>
        <title>{{ meta?.title || org.name }}</title>
        <!-- Description, canonical, favicon and the Open Graph /
             Twitter tags are rendered by the shell (app.blade.php)
             from `meta`, where a link preview can read them. -->
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link rel="stylesheet" :href="fontsHref(layout.theme)">
    </Head>
    <div class="tiq-site" :class="[rootClass(layout.theme), { 'has-preview-banner': is_preview }]" :style="rootStyle(layout.theme)">
        <TemplatePreviewBar :theme="layout.theme" />
        <div v-if="is_preview" class="tiq-preview-banner">
            <span>{{ preview_reason || 'Preview — this site is currently hidden from visitors.' }}</span>
        </div>

        <template v-for="block in headerBlocks" :key="`sh-${block.id}`">
            <component :is="COMPONENTS[block.type]"
                       :config="block.config"
                       :accent="accent"
                       :org="org"
                       :site_data="site_data" />
        </template>

        <main v-if="isTrailhead" class="th-index-page">
            <ThNewsIndex :posts="posts" :accent="accent" :org="org" />
        </main>
        <main v-else class="tiq-news-index">
            <header class="tiq-nix-header" :style="{ borderLeftColor: accent }">
                <span class="tiq-nix-eyebrow" :style="{ color: accent }">Latest</span>
                <h1>News</h1>
                <p>Trail updates, ride reports, and announcements.</p>
            </header>

            <section v-if="posts.length === 0" class="tiq-nix-empty">
                <p>{{ NO_POSTS_YET }}</p>
            </section>

            <section v-for="group in groups" :key="group.label" class="tiq-nix-group">
                <h2>{{ group.label }}</h2>
                <a v-for="p in group.items" :key="p.id" :href="p.href" class="tiq-nix-card"
                   :style="p.hero_image ? { backgroundImage: `url(${p.hero_image})` } : null"
                   :class="{ 'has-image': !!p.hero_image }">
                    <div class="tiq-nix-overlay">
                        <span class="tiq-nix-date" :style="{ color: accent }">{{ fmtDate(p.published_at) }}</span>
                        <h3>{{ p.title }}</h3>
                        <p v-if="p.excerpt" class="tiq-nix-excerpt">{{ p.excerpt }}</p>
                        <span v-if="p.author_name" class="tiq-nix-author">By {{ p.author_name }}</span>
                    </div>
                </a>
            </section>
        </main>

        <template v-for="block in footerBlocks" :key="`sf-${block.id}`">
            <component :is="COMPONENTS[block.type]"
                       :config="block.config"
                       :accent="accent"
                       :org="org"
                       :site_data="site_data" />
        </template>
    </div>
</template>

<style>
.tiq-site { font-family: 'Barlow', sans-serif; color: #1a1a1a; background: #f8f8f8; min-height: 100vh; }
.tiq-site * { box-sizing: border-box; }
.tiq-preview-banner {
    position: fixed; top: 0; left: 0; right: 0; z-index: 9999;
    background: #fde68a; color: #78350f; text-align: center;
    padding: 8px 16px;
    font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase; letter-spacing: 0.1em;
    font-size: 12px; font-weight: 600; height: 32px;
}
.tiq-site.has-preview-banner { padding-top: 32px; }
.tiq-site.has-preview-banner .tiq-nav { top: 32px !important; }
</style>

<style scoped>
/* Top padding clears the 68px fixed MenuBlock (same offset as
   EventsIndex). */
.tiq-news-index { max-width: 1200px; margin: 0 auto; padding: 124px 24px 96px; }
.has-preview-banner .tiq-news-index { padding-top: 156px; }

.tiq-nix-header {
    border-left: 4px solid currentColor;
    padding-left: 16px;
    margin-bottom: 48px;
    max-width: 880px;
}
.tiq-nix-eyebrow {
    display: block;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 12px; font-weight: 700;
    letter-spacing: 0.32em; text-transform: uppercase;
    margin-bottom: 10px;
}
.tiq-nix-header h1 {
    font-family: 'Bebas Neue', sans-serif; font-weight: bold;
    font-size: clamp(48px, 7vw, 80px);
    letter-spacing: 0.04em; line-height: 1; margin: 0;
}
.tiq-nix-header p { margin: 12px 0 0; color: #6b7280; font-size: 17px; }

.tiq-nix-empty {
    background: #fff; border: 1px dashed #d1d5db; border-radius: 6px;
    padding: 40px; text-align: center; color: #9ca3af;
}

.tiq-nix-group { margin-bottom: 40px; }
.tiq-nix-group h2 {
    font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase; letter-spacing: 0.12em;
    font-size: 13px; color: #6b7280; font-weight: 700;
    margin: 0 0 16px; padding: 0 4px;
}

/* Card grid: 3 cols wide, 2 cols medium, 1 col mobile. Imageless
   posts get a dark fallback so the title stays legible. */
.tiq-nix-group > .tiq-nix-card,
.tiq-nix-group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 18px;
}
.tiq-nix-group h2 { grid-column: 1 / -1; margin-bottom: 0; }

.tiq-nix-card {
    aspect-ratio: 4 / 3;
    background: #374151;
    background-size: cover;
    background-position: center;
    border-radius: 6px;
    overflow: hidden;
    text-decoration: none;
    color: #fff;
    position: relative;
    display: block;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.tiq-nix-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.12); }
.tiq-nix-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0.1) 30%, rgba(0,0,0,0.88) 100%);
    padding: 18px;
    display: flex; flex-direction: column; justify-content: flex-end;
}
.tiq-nix-date {
    display: block;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 12px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    margin-bottom: 6px;
}
.tiq-nix-card h3 {
    font-family: 'Bebas Neue', sans-serif; font-weight: bold;
    font-size: 24px; letter-spacing: 0.02em;
    margin: 0 0 6px; line-height: 1.1;
}
.tiq-nix-excerpt {
    font-family: 'Barlow', sans-serif;
    font-size: 13px; line-height: 1.45;
    margin: 0 0 6px;
    color: rgba(255,255,255,0.92);
    max-height: 0; overflow: hidden;
    transition: max-height 0.25s ease;
}
.tiq-nix-card:hover .tiq-nix-excerpt { max-height: 120px; }
.tiq-nix-author {
    font-family: 'Barlow', sans-serif;
    font-size: 11px; color: rgba(255,255,255,0.65);
}

@media (max-width: 600px) {
    .tiq-news-index { padding-left: 16px; padding-right: 16px; }
    .tiq-nix-group { grid-template-columns: 1fr; }
}
</style>
